/**
 * FERP PDF Comment Thread (DRR-079)
 *
 * Threaded replies on selected annotation. Shows annotation details + reply list.
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - Backend Snowflake IDs (reply IDs pending until persisted)
 * - useAuth() for reply author
 */

import { useState, useCallback } from 'react';
import {
  Stack,
  Box,
  Text,
  Paper,
  TextInput,
  Button,
  Group,
  Badge,
  Divider,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { useAuth } from '@/auth';
import { usePdfSuite } from './PdfSuiteContext';

export function CommentThread() {
  const { state, updateAnnotation } = usePdfSuite();
  const { annotations, selectedAnnotationId } = state;
  const { user } = useAuth();
  const [replyText, setReplyText] = useState('');

  const selected = annotations.find((a) => a.id === selectedAnnotationId);

  const handleAddReply = useCallback(() => {
    if (!selected || !replyText.trim()) return;
    const reply = {
      id: `pending_reply_${Date.now()}`,
      content: replyText.trim(),
      createdBy: user?.id ?? 'unknown',
      createdAt: new Date().toISOString(),
    };
    updateAnnotation({
      ...selected,
      replies: [...(selected.replies ?? []), reply],
    });
    setReplyText('');
  }, [selected, replyText, user?.id, updateAnnotation]);

  if (!selected) {
    return (
      <Box p="sm">
        <Text size="xs" c="dimmed" ta="center">
          Select an annotation to view comments
        </Text>
      </Box>
    );
  }

  const replies = selected.replies ?? [];

  return (
    <Stack gap="xs" p="xs">
      {/* Selected annotation header */}
      <Paper withBorder p="xs">
        <Group gap={4}>
          <Badge size="xs" variant="light">
            {selected.type}
          </Badge>
          <Text size="xs" c="dimmed">
            Page {selected.page}
          </Text>
        </Group>
        {selected.content && (
          <Text size="sm" mt={4}>
            {selected.content}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt={4}>
          {selected.createdBy} &middot;{' '}
          {new Date(selected.createdAt).toLocaleDateString()}
        </Text>
      </Paper>

      <Divider label={`${replies.length} replies`} labelPosition="center" />

      {/* Reply list */}
      {replies.map((reply) => (
        <Paper key={reply.id} withBorder p="xs" style={{ marginLeft: 12 }}>
          <Text size="sm">{reply.content}</Text>
          <Text size="xs" c="dimmed" mt={2}>
            {reply.createdBy} &middot;{' '}
            {new Date(reply.createdAt).toLocaleDateString()}
          </Text>
        </Paper>
      ))}

      {/* Reply input */}
      <Group gap="xs">
        <TextInput
          size="xs"
          placeholder="Add reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddReply();
          }}
          style={{ flex: 1 }}
        />
        <Button
          size="xs"
          variant="light"
          leftSection={<IconSend size={12} />}
          disabled={!replyText.trim()}
          onClick={handleAddReply}
        >
          Reply
        </Button>
      </Group>
    </Stack>
  );
}
